import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Payments, PersonSearch, School } from '@mui/icons-material';
import CalendarPage from '../Calendar';
import { authGet, authPost, authPatch } from '../../lib/api';
import { dateOnly, formatDate, todayDate } from '../../lib/dateFormat';
import { referralSourceOptionsFor } from '../../lib/referralSources';
import { moveStudentDestinationClasses } from '../Rosters/moveStudentDestinations';

// The Intake route is a calendar-only workspace; retain the workflow implementation without rendering it here.
const SHOW_UNIFIED_INTAKE_WORKFLOW = false;

function formatMoney(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPhone(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatStudentLabel(student) {
  if (!student) return '';
  const name = [student.first_name, student.middle_name, student.last_name].filter(Boolean).join(' ');
  const bits = [
    student.code ? `Code ${student.code}` : '',
    student.birth_date ? `DOB ${formatDate(student.birth_date)}` : '',
    student.phone ? `Phone ${student.phone}` : '',
    student.cause_number ? `Cause ${student.cause_number}` : ''
  ].filter(Boolean);
  return `${name || 'Unnamed student'}${bits.length ? ` — ${bits.join(' • ')}` : ''}`;
}

function formatRecentTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

const recentActionLabels = {
  search: 'Search',
  select_student: 'Opened',
  create_student: 'Created',
  update_student: 'Edited',
  delete_student: 'Deleted',
  enroll_student: 'Enrolled',
  collect_payment: 'Payment',
  move_student: 'Moved',
  remove_student: 'Removed',
  print_exit_pdf: 'PDF Export'
};

const recentActionColors = {
  search: 'default',
  select_student: 'info',
  create_student: 'success',
  update_student: 'warning',
  delete_student: 'error',
  enroll_student: 'success',
  collect_payment: 'success',
  move_student: 'secondary',
  remove_student: 'error',
  print_exit_pdf: 'info'
};

const emptyStudentForm = {
  code: '',
  ssn: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  birth_date: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  driver_number: '',

  cause_number: '',
  county_of_conviction: '',
  probation_officer_name: '',
  probation_officer_email: '',
  probation_officer_phone: '',
  attorney_name: '',
  attorney_email: '',
  attorney_phone: '',
  reporting_agency: '',
  referral_source: '',
  state_reporting_notes: '',
  call_notes: ''
};

function studentToForm(student = {}) {
  return {
    ...emptyStudentForm,
    ...student,
    birth_date: dateOnly(student.birth_date),
    ssn: String(student.ssn_last4 || student.ssn || '').replace(/\D/g, '').slice(-4)
  };
}

export default function IntakePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'recents' ? 'recents' : 'workflow';
  const [activeIntakeTab, setActiveIntakeTab] = useState(initialTab);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentForm, setStudentForm] = useState(emptyStudentForm);
  const [studentDetail, setStudentDetail] = useState({ history: [], payments: [] });
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: todayDate(), method: '', reference_number: '', notes: '' });
  const [transferDialog, setTransferDialog] = useState(null);
  const [recents, setRecents] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const loadScheduledClasses = useCallback(async () => {
    const data = await authGet('/api/scheduled-classes?status=open');
    setScheduledClasses(data.scheduledClasses || []);
  }, []);

  const loadStudentDetail = async (studentId) => {
    if (!studentId) return;
    const [detail, payments] = await Promise.all([
      authGet(`/api/students/${studentId}`),
      authGet(`/api/payments/student/${studentId}`)
    ]);
    setSelectedStudent(detail.student);
    setStudentForm(studentToForm(detail.student));
    setStudentDetail({ history: detail.history || [], payments: payments.payments || [] });
  };

  const upsertStudentOption = useCallback((student) => {
    if (!student?.id) return;
    setStudentOptions((current) => [
      student,
      ...current.filter((option) => option.id !== student.id)
    ]);
  }, []);

  const refreshStudentSearch = useCallback(async (query, includeStudent = null) => {
    const q = query.trim();
    if (!q) {
      setStudentOptions(includeStudent?.id ? [includeStudent] : []);
      return [];
    }

    const data = await authGet(`/api/students/search/list?q=${encodeURIComponent(q)}&limit=20`);
    const students = data.students || [];
    const merged = includeStudent?.id
      ? [includeStudent, ...students.filter((student) => student.id !== includeStudent.id)]
      : students;
    setStudentOptions(merged);
    return merged;
  }, []);

  const loadRecents = useCallback(async () => {
    setRecentsLoading(true);
    try {
      const data = await authGet('/api/intake-recents?limit=30');
      setRecents(data.recents || []);
    } catch (err) {
      setError(err.message || 'Unable to load recent intake activity');
    } finally {
      setRecentsLoading(false);
    }
  }, []);


  useEffect(() => {
    loadScheduledClasses().catch((err) => setError(err.message || 'Unable to load classes'));
    loadRecents().catch((err) => setError(err.message || 'Unable to load recent intake activity'));
  }, [loadScheduledClasses, loadRecents]);

  useEffect(() => {
    if (selectedClassId && !scheduledClasses.some((item) => item.id === selectedClassId)) {
      setSelectedClassId('');
    }
  }, [scheduledClasses, selectedClassId]);

  useEffect(() => {
    setActiveIntakeTab(searchParams.get('tab') === 'recents' ? 'recents' : 'workflow');
  }, [searchParams]);

  const handleIntakeTabChange = (_, value) => {
    setActiveIntakeTab(value);
    setSearchParams(value === 'recents' ? { tab: 'recents' } : {});
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      const q = studentQuery.trim();
      if (!q) {
        setStudentOptions([]);
        return;
      }
      setStudentLoading(true);
      try {
        await refreshStudentSearch(q);
      } catch (err) {
        setStudentOptions([]);
        setError(err.message || 'Unable to search students');
      } finally {
        setStudentLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [studentQuery, refreshStudentSearch]);

  const selectedClass = useMemo(
    () => scheduledClasses.find((item) => item.id === selectedClassId) || null,
    [scheduledClasses, selectedClassId]
  );

  const transferDestinationClasses = useMemo(() => {
    if (!transferDialog?.sourceClassId) return [];
    return moveStudentDestinationClasses(scheduledClasses, {
      sourceClassId: transferDialog.sourceClassId,
      destinationType: transferDialog.destination_type,
      today: todayDate()
    });
  }, [scheduledClasses, transferDialog?.sourceClassId, transferDialog?.destination_type]);

  const selectedTransferDestinationClass = useMemo(
    () => scheduledClasses.find((item) => item.id === transferDialog?.destination_class_id) || null,
    [scheduledClasses, transferDialog?.destination_class_id]
  );

  const handleSelectStudent = async (_, student) => {
    setError('');
    setStatus('');
    setSelectedStudent(student || null);
    setStudentForm(student ? studentToForm(student) : emptyStudentForm);
    setStudentDetail({ history: [], payments: [] });
    if (student?.id) {
      await loadStudentDetail(student.id);
      await loadRecents();
    }
  };

  const handleNewStudent = () => {
    setSelectedStudent(null);
    setStudentForm({ ...emptyStudentForm, first_name: studentQuery });
    setStudentDetail({ history: [], payments: [] });
    setStatus('Creating a new student record. Fill the form, then Save Student.');
  };

  const handleSaveStudent = async () => {
    setError('');
    setStatus('');
    try {
      const payload = {
        ...studentForm,
        cause_number: studentForm.cause_number || '',
        phone: formatPhone(studentForm.phone),
        probation_officer_phone: formatPhone(studentForm.probation_officer_phone),
        attorney_phone: formatPhone(studentForm.attorney_phone)
      };
      const wasUpdate = Boolean(selectedStudent?.id);
      const data = wasUpdate
        ? await authPatch(`/api/students/${selectedStudent.id}`, payload)
        : await authPost('/api/students', payload);
      const student = data.student;
      const savedStudentLabel = formatStudentLabel(student);
      setStatus(wasUpdate ? 'Student updated.' : 'Student created.');
      await loadStudentDetail(student.id);
      upsertStudentOption(student);
      setStudentQuery(savedStudentLabel);
      await refreshStudentSearch(savedStudentLabel, student);
      await loadRecents();
    } catch (err) {
      setError(err.message || 'Unable to save student');
    }
  };

  const handleEnroll = async () => {
    if (!selectedStudent?.id || !selectedClassId) {
      setError('Select a student and a class before enrolling.');
      return;
    }
    setError('');
    try {
      await authPost(`/api/scheduled-classes/${selectedClassId}/students`, {
        student_id: selectedStudent.id,
        admin_override: true
      });
      setStatus('Student enrolled in selected class.');
      await Promise.all([loadScheduledClasses(), loadStudentDetail(selectedStudent.id)]);
      await loadRecents();
    } catch (err) {
      setError(err.message || 'Unable to enroll student');
    }
  };

  const openTransferDialog = (historyRow) => {
    if (!selectedStudent?.id || !historyRow?.id) {
      setError('Select a student and source class before moving.');
      return;
    }
    setError('');
    setStatus('');
    setTransferDialog({
      sourceClassId: historyRow.id,
      sourceClassName: historyRow.class_name || historyRow.class_description || 'this class',
      studentId: selectedStudent.id,
      destination_type: 'class',
      destination_class_id: '',
      notes: ''
    });
  };

  const submitTransfer = async () => {
    if (!transferDialog?.sourceClassId || !transferDialog?.studentId) return;
    if (!transferDialog.destination_class_id) {
      setError(transferDialog.destination_type === 'holding_tank'
        ? 'Select a holding tank class.'
        : 'Select a destination class.');
      return;
    }

    setError('');
    setStatus('');
    try {
      const result = await authPost(`/api/rosters/${transferDialog.sourceClassId}/transfer`, {
        student_ids: [transferDialog.studentId],
        destination_type: transferDialog.destination_type,
        destination_class_id: transferDialog.destination_class_id,
        notes: transferDialog.notes || (selectedTransferDestinationClass?.class_name
          ? `Moved to ${selectedTransferDestinationClass.class_name}`
          : '')
      });
      setTransferDialog(null);
      setStatus(result.message || 'Student moved. Prior class history remains attached.');
      await Promise.all([loadScheduledClasses(), loadStudentDetail(transferDialog.studentId)]);
      await loadRecents();
    } catch (err) {
      setError(err.message || 'Unable to move student');
    }
  };

  const handleCollectPayment = async () => {
    if (!selectedStudent?.id) {
      setError('Select or create a student before collecting payment.');
      return;
    }
    const amount = Number(paymentForm.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a payment amount greater than $0.');
      return;
    }
    setError('');
    try {
      await authPost('/api/payments', {
        student_id: selectedStudent.id,
        scheduled_class_id: selectedClassId || null,
        amount,
        payment_date: paymentForm.payment_date || null,
        method: paymentForm.method || '',
        reference_number: paymentForm.reference_number || '',
        notes: paymentForm.notes || (selectedClass ? `Intake payment for ${selectedClass.class_name}` : 'Intake payment')
      });
      setPaymentForm({ amount: '', payment_date: todayDate(), method: '', reference_number: '', notes: '' });
      setStatus('Payment collected.');
      await loadStudentDetail(selectedStudent.id);
      await loadRecents();
    } catch (err) {
      setError(err.message || 'Unable to collect payment');
    }
  };

  const historyColumns = [
    { field: 'completed_date', headerName: 'Date', width: 125, valueGetter: (_, row = {}) => formatDate(row.completed_date) },
    { field: 'class_code', headerName: 'Code', width: 100 },
    { field: 'class_name', headerName: 'Class', flex: 1, minWidth: 210 },
    { field: 'instructor', headerName: 'Instructor', flex: 0.75, minWidth: 170 },
    { field: 'cause_number', headerName: 'Cause #', width: 140 },
    { field: 'tx_cert_number', headerName: 'Cert #', width: 140 },
    {
      field: 'move_action',
      headerName: 'Move To',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button size="small" variant="outlined" onClick={() => openTransferDialog(params.row)}>
          Move To
        </Button>
      )
    }
  ];

  const paymentColumns = [
    { field: 'payment_date', headerName: 'Date', width: 125, valueGetter: (_, row = {}) => formatDate(row.payment_date) },
    { field: 'amount', headerName: 'Amount', width: 110, valueGetter: (_, row = {}) => formatMoney(row.amount) },
    { field: 'method', headerName: 'Method', minWidth: 125, flex: 0.6 },
    { field: 'reference_number', headerName: 'Reference', minWidth: 150, flex: 0.7 },
    { field: 'class_name', headerName: 'Class', minWidth: 210, flex: 1 },
    { field: 'notes', headerName: 'Notes', minWidth: 220, flex: 1 }
  ];

  return (
    <Box>
      <CalendarPage enableRecurrence showStatusFilter defaultStatusFilter={['open']} />

      {SHOW_UNIFIED_INTAKE_WORKFLOW && (
        <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4" gutterBottom>Unified Intake Workflow</Typography>
            <Typography variant="body2" color="text.secondary">
              Search or create a student, edit details, collect payment, enroll in class, and review prior classes/payments without leaving this screen.
            </Typography>
          </Box>

          {status && <Alert severity="success" onClose={() => setStatus('')}>{status}</Alert>}
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          <Tabs value={activeIntakeTab} onChange={handleIntakeTabChange} aria-label="Intake tabs">
            <Tab value="workflow" label="Intake Workflow" />
            <Tab value="recents" label="Recents" />
          </Tabs>

          {activeIntakeTab === 'recents' && (
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Box>
                  <Typography variant="h5">Your Recent Intake Activity</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Only activity recorded under your logged-in account appears here.
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={loadRecents} disabled={recentsLoading}>Refresh</Button>
              </Box>
              {recents.length === 0 ? (
                <Alert severity="info">No recent intake activity yet. Searches, opened students, edits, payments, enrollments, and moves will show here.</Alert>
              ) : (
                <Stack spacing={1.25}>
                  {recents.map((recent) => (
                    <Paper key={recent.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              label={recentActionLabels[recent.action_type] || recent.action_type}
                              color={recentActionColors[recent.action_type] || 'default'}
                            />
                            <Typography variant="body1">{recent.label}</Typography>
                          </Stack>
                          {(recent.student_name?.trim() || recent.class_name) && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                              {[recent.student_name?.trim(), recent.class_name].filter(Boolean).join(' • ')}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{formatRecentTime(recent.created_at)}</Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          {activeIntakeTab === 'workflow' && (
            <>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <Autocomplete
                options={studentOptions}
                loading={studentLoading}
                value={selectedStudent}
                inputValue={studentQuery}
                onInputChange={(_, value) => setStudentQuery(value)}
                onChange={handleSelectStudent}
                getOptionLabel={formatStudentLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Search student by name, code, phone, DOB, DL, cause, officer, attorney" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="contained" startIcon={<PersonSearch />} onClick={handleNewStudent}>
                Create If Not Found
              </Button>
            </Grid>
          </Grid>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><TextField fullWidth label="First Name" value={studentForm.first_name} onChange={(e) => setStudentForm({ ...studentForm, first_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Middle Name" value={studentForm.middle_name} onChange={(e) => setStudentForm({ ...studentForm, middle_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Last Name" value={studentForm.last_name} onChange={(e) => setStudentForm({ ...studentForm, last_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="DOB" type="date" InputLabelProps={{ shrink: true }} value={studentForm.birth_date} onChange={(e) => setStudentForm({ ...studentForm, birth_date: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="SSN Last 4" inputProps={{ maxLength: 4 }} value={studentForm.ssn} onChange={(e) => setStudentForm({ ...studentForm, ssn: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Phone" value={studentForm.phone} onChange={(e) => setStudentForm({ ...studentForm, phone: formatPhone(e.target.value) })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Email" value={studentForm.email} onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Address" value={studentForm.address} onChange={(e) => setStudentForm({ ...studentForm, address: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="City" value={studentForm.city} onChange={(e) => setStudentForm({ ...studentForm, city: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="State" value={studentForm.state} onChange={(e) => setStudentForm({ ...studentForm, state: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="Zip" value={studentForm.zip} onChange={(e) => setStudentForm({ ...studentForm, zip: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Driver Number" value={studentForm.driver_number} onChange={(e) => setStudentForm({ ...studentForm, driver_number: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Cause Number" value={studentForm.cause_number} onChange={(e) => setStudentForm({ ...studentForm, cause_number: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="County of Conviction" value={studentForm.county_of_conviction} onChange={(e) => setStudentForm({ ...studentForm, county_of_conviction: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Probation Officer" value={studentForm.probation_officer_name} onChange={(e) => setStudentForm({ ...studentForm, probation_officer_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Probation Email" value={studentForm.probation_officer_email} onChange={(e) => setStudentForm({ ...studentForm, probation_officer_email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Probation Phone" value={studentForm.probation_officer_phone} onChange={(e) => setStudentForm({ ...studentForm, probation_officer_phone: formatPhone(e.target.value) })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney" value={studentForm.attorney_name} onChange={(e) => setStudentForm({ ...studentForm, attorney_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney Email" value={studentForm.attorney_email} onChange={(e) => setStudentForm({ ...studentForm, attorney_email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney Phone" value={studentForm.attorney_phone} onChange={(e) => setStudentForm({ ...studentForm, attorney_phone: formatPhone(e.target.value) })} /></Grid>

            <Grid item xs={12}>
              <TextField select fullWidth label="Referral Source" value={studentForm.referral_source} onChange={(e) => setStudentForm({ ...studentForm, referral_source: e.target.value })}>
                <MenuItem value="">Not selected</MenuItem>
                {referralSourceOptionsFor(studentForm.referral_source).map((source) => (
                  <MenuItem key={source} value={source}>{source}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Additional State Reporting Fields" value={studentForm.state_reporting_notes} onChange={(e) => setStudentForm({ ...studentForm, state_reporting_notes: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Reporting Agency" value={studentForm.reporting_agency} onChange={(e) => setStudentForm({ ...studentForm, reporting_agency: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Call Notes" value={studentForm.call_notes} onChange={(e) => setStudentForm({ ...studentForm, call_notes: e.target.value })} /></Grid>
            <Grid item xs={12}><Button variant="contained" startIcon={<Add />} onClick={handleSaveStudent}>Save Student</Button></Grid>
          </Grid>

          <Divider />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <TextField select fullWidth label="Assign Class" value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}>
                <MenuItem value="">No class selected</MenuItem>
                {scheduledClasses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {formatDate(item.class_date)} — {item.class_name} {item.class_time ? `@ ${String(item.class_time).slice(0, 5)}` : ''} {item.location ? `— ${item.location}` : ''} ({String(item.status || 'open').toUpperCase()})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <Button fullWidth variant="contained" startIcon={<School />} onClick={handleEnroll}>Enroll in Selected Class</Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}><TextField fullWidth label="Payment Amount" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth type="date" label="Payment Date" InputLabelProps={{ shrink: true }} value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="Method" value={paymentForm.method} onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="Reference" value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Payment Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} /></Grid>
            <Grid item xs={12} md={1}><Button fullWidth variant="contained" startIcon={<Payments />} onClick={handleCollectPayment}>Pay</Button></Grid>
          </Grid>

          <Divider />

          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ minHeight: 58 }}>
                <Typography variant="h5" gutterBottom>Prior Classes</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review class history or use Move To for another class, a future class, or the holding tank.
                </Typography>
              </Box>
              <Box sx={{ height: 330, mt: 1.5 }}>
                <DataGrid rows={studentDetail.history || []} columns={historyColumns} getRowId={(row) => row.id} disableRowSelectionOnClick pageSizeOptions={[5, 10]} />
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ minHeight: 58 }}>
                <Typography variant="h5" gutterBottom>Prior Payments</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review payment dates, amounts, methods, references, classes, and notes in one aligned history table.
                </Typography>
              </Box>
              <Box sx={{ height: 330, mt: 1.5 }}>
                <DataGrid rows={studentDetail.payments || []} columns={paymentColumns} getRowId={(row) => row.id} disableRowSelectionOnClick pageSizeOptions={[5, 10]} />
              </Box>
            </Paper>
          </Stack>
            </>
          )}
        </Stack>
        </Paper>
      )}

      <Dialog open={Boolean(transferDialog)} onClose={() => setTransferDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Move Student To</DialogTitle>
        <DialogContent>
          {transferDialog && (
            <Stack spacing={2} mt={0.5}>
              <Typography variant="body2" color="text.secondary">
                Moving {selectedStudent?.first_name || 'this student'} {selectedStudent?.last_name || ''} from {transferDialog.sourceClassName}. Previous roster, attendance, and payment history remains attached to the student.
              </Typography>
              <TextField
                select
                fullWidth
                label="Destination"
                value={transferDialog.destination_type}
                onChange={(e) => setTransferDialog({
                  ...transferDialog,
                  destination_type: e.target.value,
                  destination_class_id: ''
                })}
              >
                <MenuItem value="class">Different Class</MenuItem>
                <MenuItem value="holding_tank">Holding Tank</MenuItem>
                <MenuItem value="future_class">Future Class</MenuItem>
              </TextField>
              <TextField
                select
                required
                fullWidth
                label={transferDialog.destination_type === 'holding_tank' ? 'Holding Tank Class' : 'Destination Class'}
                helperText={transferDialog.destination_type === 'holding_tank'
                  ? 'Showing existing classes with “holding” or “tank” in the class name/code.'
                  : ''}
                value={transferDialog.destination_class_id}
                onChange={(e) => setTransferDialog({ ...transferDialog, destination_class_id: e.target.value })}
              >
                {transferDestinationClasses.length === 0 && (
                  <MenuItem value="" disabled>
                    {transferDialog.destination_type === 'holding_tank'
                      ? 'No holding tank classes found'
                      : 'No destination classes found'}
                  </MenuItem>
                )}
                {transferDestinationClasses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {formatDate(item.class_date)} — {item.class_name} {item.class_time ? `@ ${String(item.class_time).slice(0, 5)}` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Transfer Notes"
                value={transferDialog.notes}
                onChange={(e) => setTransferDialog({ ...transferDialog, notes: e.target.value })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitTransfer}>Move</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
